import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useCampaign, useCampaignId, useTypes } from '../hooks';
import { t, tDyn } from '../i18n';
import { MembersSettings } from '../components/MembersSettings';
import { clearOfflineData, pullCampaign, useOfflineState } from '../offline';
import { localCopyInfo, resetLocalCopy } from '../offline/sync';

/** Stand der lokalen Kopie auf diesem Gerät (Phase 4). */
function OfflineSettings({ cid }: { cid: string }) {
  const s = useOfflineState();
  const [info, setInfo] = useState<Awaited<ReturnType<typeof localCopyInfo>> | null>(null);
  const [busy, setBusy] = useState(false);
  const last = s.lastSync[cid];
  useEffect(() => {
    void localCopyInfo(cid).then(setInfo);
  }, [cid, last]);
  const run = (fn: () => Promise<void>) => {
    setBusy(true);
    void fn().finally(() => setBusy(false));
  };
  return (
    <section className="card offline-info">
      <h2>{t('offline.statusTitle')}</h2>
      <p className="small muted">{t('offline.statusIntro')}</p>
      <p>
        {t('offline.lastSync')}: <strong>{last ? new Date(last).toLocaleString('de-DE') : t('offline.never')}</strong>
        {!s.online && <> · {t('offline.offline')}</>}
        {s.pending > 0 && <> · {t('offline.pending', { n: s.pending })}</>}
      </p>
      {info && <p className="small muted">{t('offline.copy', { notes: info.notes, maps: info.maps, combats: info.combats, files: info.files })}</p>}
      <div className="row">
        <button disabled={busy || !s.online} onClick={() => run(() => pullCampaign(cid))}>
          {t('offline.syncNow')}
        </button>
        <button disabled={busy || !s.online} onClick={() => run(() => resetLocalCopy(cid))}>
          {t('offline.reload')}
        </button>
      </div>
      <p className="small muted">
        <strong>{t('offline.install')}:</strong> {t('offline.installHint')}
      </p>
    </section>
  );
}

function ScreenSettings({ cid }: { cid: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['screen', cid], queryFn: () => api.screenSettings(cid) });
  const [copied, setCopied] = useState(false);
  const set = (p: Promise<unknown>) => void p.then((res) => qc.setQueryData(['screen', cid], res));
  const s = q.data;
  const link = s?.token ? `${location.origin}/screen/${s.token}` : '';
  return (
    <section className="card screen-settings">
      <h2>{t('screen.title')}</h2>
      <p className="small muted">{t('screen.intro')}</p>
      {!s?.enabled ? (
        <>
          <p className="muted">{t('screen.off')}</p>
          <button onClick={() => set(api.updateScreen(cid, {}))}>{t('screen.create')}</button>
        </>
      ) : (
        <>
          <label className="sb-field">
            <span>{t('screen.link')}</span>
            <span className="row">
              <input readOnly value={link} onFocus={(e) => e.target.select()} className="screen-link" />
              <button
                onClick={() =>
                  void navigator.clipboard.writeText(link).then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  })
                }
              >
                {copied ? t('screen.copied') : t('screen.copy')}
              </button>
              <a className="button" href={link} target="_blank" rel="noreferrer">
                {t('screen.open')}
              </a>
            </span>
          </label>
          <p>
            {t('screen.map')}: <strong>{s.mapTitle ?? t('screen.noMap')}</strong>{' '}
            {s.mapNoteId && (
              <button className="small" onClick={() => set(api.updateScreen(cid, { mapNoteId: null, view: null }))}>
                {t('screen.clearMap')}
              </button>
            )}
          </p>
          <div className="row">
            <button onClick={() => confirm(t('screen.rotateConfirm')) && set(api.rotateScreen(cid))}>{t('screen.rotate')}</button>
            <button className="danger" onClick={() => confirm(t('screen.disableConfirm')) && set(api.disableScreen(cid))}>
              {t('screen.disable')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export function SettingsPage() {
  const cid = useCampaignId();
  const campaign = useCampaign(cid);
  const types = useTypes(cid);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [ruleset, setRuleset] = useState('mixed');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (campaign.data) {
      setName(campaign.data.name);
      setRuleset(campaign.data.ruleset);
    }
  }, [campaign.data]);

  const saveCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.updateCampaign(cid, { name, ruleset });
      await qc.invalidateQueries({ queryKey: ['campaign', cid] });
      await qc.invalidateQueries({ queryKey: ['campaigns'] });
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const updateType = async (key: string, patch: { color?: string; label?: string }) => {
    await api.updateType(cid, key, patch);
    await qc.invalidateQueries({ queryKey: ['types', cid] });
  };

  const auth = useQuery({ queryKey: ['auth'], queryFn: api.authState, staleTime: Infinity });
  const myId = auth.data?.user?.id ?? '';
  const role = campaign.data?.role;

  const leave = async () => {
    if (!campaign.data || !confirm(t('members.leaveConfirm', { name: campaign.data.name }))) return;
    try {
      await api.removeMember(cid, myId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      return;
    }
    await clearOfflineData();
    qc.clear();
    navigate('/');
  };

  const remove = async () => {
    if (!campaign.data || !confirm(t('campaigns.deleteConfirm', { name: campaign.data.name }))) return;
    await api.deleteCampaign(cid);
    qc.clear();
    navigate('/');
  };

  // Erst nach dem Laden der Rolle entscheiden, was angezeigt (und abgefragt) wird
  if (!campaign.data) return <div className="muted pad">{t('common.loading')}</div>;
  if (role === 'player') {
    return (
      <div className="page-narrow">
        <h1>{t('settings.title')}</h1>
        <OfflineSettings cid={cid} />
        <section className="card">
          <button className="danger" onClick={leave}>
            {t('members.leave')}
          </button>
          {error && <div className="error">{error}</div>}
        </section>
      </div>
    );
  }

  return (
    <div className="page-narrow">
      <h1>{t('settings.title')}</h1>
      <form className="card row-form" onSubmit={saveCampaign}>
        <label>
          {t('common.name')}
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label>
          {t('campaigns.ruleset')}
          <select value={ruleset} onChange={(e) => setRuleset(e.target.value)}>
            {['5e-2014', '5e-2024', 'mixed'].map((r) => (
              <option key={r} value={r}>
                {tDyn(`ruleset.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <button className="primary">{t('common.save')}</button>
        {error && <div className="error">{error}</div>}
      </form>
      {role === 'dm' && myId && <MembersSettings cid={cid} myId={myId} />}
      <ScreenSettings cid={cid} />
      <OfflineSettings cid={cid} />

      <section className="card">
        <h2>{t('settings.types')}</h2>
        <table className="table">
          <thead>
            <tr>
              <th>{t('settings.color')}</th>
              <th>{t('settings.label')}</th>
              <th>Key</th>
            </tr>
          </thead>
          <tbody>
            {types.data?.map((ty) => (
              <tr key={ty.key}>
                <td>
                  <input type="color" defaultValue={ty.color} onBlur={(e) => e.target.value !== ty.color && updateType(ty.key, { color: e.target.value })} />
                </td>
                <td>
                  <input defaultValue={ty.label} onBlur={(e) => e.target.value !== ty.label && updateType(ty.key, { label: e.target.value })} />
                </td>
                <td className="muted small">{ty.key}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card danger-zone">
        <h2>{t('settings.danger')}</h2>
        <div className="row">
          {role === 'dm' && (
            <button className="danger" onClick={remove}>
              {t('settings.deleteCampaign')}
            </button>
          )}
          <button className="danger" onClick={leave}>
            {t('members.leave')}
          </button>
        </div>
      </section>
    </div>
  );
}
